import { useEffect, useState } from "react";
import { Eye, EyeOff, LayoutDashboard, Target, TrendingUp, Gauge, Save, Check, BarChart3, Flame } from "lucide-react";
import api from "../lib/api";
import { useDashboardVisibility } from "../lib/visibility";

const DASHBOARDS = [
  {
    key: "crm_overview",
    label: "CRM Overview",
    desc: "Vista global del pipeline de leads (PowerBI / Dynamics).",
    icon: <LayoutDashboard size={18}/>,
    accent: "#111827",
  },
  {
    key: "leads_performance",
    label: "Lead Performance",
    desc: "Canales digitales en tiempo real (Zapier, Meta, Google Ads).",
    icon: <Target size={18}/>,
    accent: "#f4c400",
  },
  {
    key: "predictive",
    label: "Forecast e Inventario",
    desc: "Forecast 3m, calculadora de meta, alertas de stock e inventario.",
    icon: <TrendingUp size={18}/>,
    accent: "#3b82f6",
  },
  {
    key: "scorecard",
    label: "Scorecard ND",
    desc: "KPIs comerciales, campañas y comparador. Vista ejecutiva mensual.",
    icon: <Gauge size={18}/>,
    accent: "#8b5cf6",
  },
  {
    key: "zonas_hot",
    label: "Zonas Hot SDM",
    desc: "Ventas de Zonas Hot por sucursal, showroom y mes.",
    icon: <Flame size={18}/>,
    accent: "#ea580c",
  },
  {
    key: "sales_performance",
    label: "Sales Performance",
    desc: "Command center comercial. Ventas, funnel y comparativas para Gerentes de Ventas.",
    icon: <BarChart3 size={18}/>,
    accent: "#dc2626",
  },
];

export default function DashboardVisibilityCard() {
  const { flags: ctxFlags, refresh } = useDashboardVisibility();
  const [flags, setFlags] = useState(ctxFlags);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Keep local state in sync with the provider after initial load / external refresh.
  useEffect(() => { setFlags(ctxFlags); }, [ctxFlags]);

  const dirty = DASHBOARDS.some((d) => flags[d.key] !== ctxFlags[d.key]);

  const toggle = (key) => {
    setFlags((p) => ({ ...p, [key]: !p[key] }));
    setSaved(false);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put("/admin/dashboard-visibility", flags);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface p-6" data-testid="dashboard-visibility-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="kpi-icon-clean" style={{ color: "#7c3aed", boxShadow: "inset 0 0 0 1px #7c3aed1f" }}>
            <Eye size={18}/>
          </div>
          <div>
            <h3 className="font-display font-extrabold text-lg">Visibilidad de Dashboards</h3>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              Controla qué dashboards ven los usuarios no-admin. Los admin siempre ven todo.
            </p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="px-5 py-2 rounded-lg font-extrabold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: dirty ? "#111827" : "#e2e8f0", color: dirty ? "#f4c400" : "#94a3b8" }}
          data-testid="visibility-save-btn"
        >
          {saved ? <><Check size={14}/> Guardado</> : <><Save size={14}/> {saving ? "Guardando…" : "Guardar cambios"}</>}
        </button>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 rounded-lg text-xs font-bold" style={{ background: "#fee2e2", color: "#991b1b" }} data-testid="visibility-error">
          {error}
        </div>
      )}

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {DASHBOARDS.map((d) => {
          const isOn = flags[d.key] !== false;
          return (
            <div key={d.key} className="surface p-4 flex items-start gap-3"
                 style={{ borderLeft: `3px solid ${isOn ? d.accent : "#cbd5e1"}`, opacity: isOn ? 1 : 0.7 }}
                 data-testid={`visibility-row-${d.key}`}>
              <div className="kpi-icon-clean shrink-0" style={{ color: d.accent, boxShadow: `inset 0 0 0 1px ${d.accent}1f` }}>
                {d.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-display font-extrabold text-sm">{d.label}</span>
                  <span className="pill text-[10px] font-extrabold" style={{
                    background: isOn ? "#dcfce7" : "#fee2e2",
                    color:      isOn ? "#166534" : "#991b1b",
                    padding: "2px 8px",
                  }}>
                    {isOn ? "VISIBLE" : "OCULTO"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 font-semibold">{d.desc}</p>
              </div>
              <button
                onClick={() => toggle(d.key)}
                className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-extrabold flex items-center gap-1.5"
                style={{
                  background: isOn ? "#fef3c7" : "#f1f5f9",
                  color:      isOn ? "#92400e" : "#475569",
                }}
                data-testid={`visibility-toggle-${d.key}`}
                aria-label={`Toggle ${d.label}`}
              >
                {isOn ? <><Eye size={12}/> ON</> : <><EyeOff size={12}/> OFF</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
